import { faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BadgeProps } from '@zinc/ui';
import {
  Badge,
  Button,
  DropdownMenu,
  FormControl,
  Text,
  TextField,
  View,
  Checkbox as ZincCheckbox,
  Select as ZincSelect
} from '@zinc/ui';
import type React from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cache } from 'tm3-core/cache';
import { VirtualList } from './field-select/VirtualList';

export interface IProps<T> {
  autoWidth?: boolean;
  className?: string;
  defaultDisplayText?: string;
  disabledFunction?: (item: Object) => boolean;
  displayMember?: string;
  secondaryDisplayMember?: string;
  dLabel?: boolean;
  errored?: false;
  errorText?: string;
  fullWidth?: boolean;
  includeNone?: false;
  items?: Object[];
  label?: string;
  lookup?: boolean;
  lookupItems?: T[];
  noneText?: string;
  noneValue?: T;
  value?: T[];
  valueMember?: string;
  warning?: false;
  dataTestId?: string;
  applyLabel?: string;
  placeholder?: string;
  allowCustomValues?: boolean;
  customValuePlaceholder?: string;
  badgeColor?: BadgeProps['color'];
  showApplyButton?: boolean;
  showSelectAll?: boolean;
  submitted?: boolean;
  removeLegacyPadding?: boolean;
  onIsOpenChange?: (isOpen: boolean) => void;

  onChange: (value: T[]) => any;
}

const FieldMultiSelect: React.FC<IProps<number> | IProps<string>> = (props) => {
  return <ZincFieldMultiSelect {...props} />;
};

const ZincFieldMultiSelect: React.FC<IProps<number> | IProps<string>> = (props) => {
  const {
    className,
    displayMember = 'text',
    errorText,
    valueMember = 'payload',
    applyLabel = 'Apply',
    placeholder = 'Select a value',
    secondaryDisplayMember,
    label,
    items: propItems,
    lookup,
    submitted,
    value,
    showSelectAll = false,
    removeLegacyPadding = false,
    allowCustomValues = false,
    customValuePlaceholder = 'Add an option',
    badgeColor = 'primary',
    showApplyButton = true,
    onChange,
    onIsOpenChange
  } = props;

  const lookupValues = useMemo(() => {
    return lookup ? (cache.lookups.GetLookups(lookup)?.values ?? []) : [];
  }, [lookup]);

  const items = useMemo<Record<string, string | number>[] | string[]>(
    () => (lookup ? lookupValues : (propItems as Record<string, string | number>[]) || []),
    [lookup, lookupValues, propItems]
  );
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = useCallback(() => {
    setIsOpen(true);
    onIsOpenChange?.(true);
  }, [onIsOpenChange]);
  const [selectedItems, setSelectedItems] = useState(value || ([] as (string | number)[]));
  const [customValue, setCustomValue] = useState('');
  const skipNextCloseRef = useRef(false);

  useEffect(() => {
    setSelectedItems(value || ([] as (string | number)[]));
  }, [value]);

  const onClose = useCallback(() => {
    if (skipNextCloseRef.current) {
      skipNextCloseRef.current = false;
      return;
    }

    setIsOpen(false);
    onIsOpenChange?.(false);
    onChange(selectedItems as string[] & number[]);
  }, [selectedItems, onChange, onIsOpenChange]);

  const onItemInteractionStart = useCallback(() => {
    skipNextCloseRef.current = true;
  }, []);

  const onItemKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      skipNextCloseRef.current = true;
    }
  }, []);

  const isEqual = useCallback((a: string | number, b: string | number) => {
    // Compare as numbers if both are numeric else as strings
    if (!isNaN(Number(a)) && !isNaN(Number(b))) return Number(a) === Number(b);

    return String(a) === String(b);
  }, []);

  const isSelected = useCallback(
    (item: string | number) => selectedItems.some((x) => isEqual(item, x)),
    [selectedItems, isEqual]
  );

  const removeItem = useCallback(
    (value: string | number) => selectedItems.filter((x) => !isEqual(x, value)),
    [selectedItems, isEqual]
  );

  const onSelect = useCallback(
    (
      e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
      value: string | number
    ) => {
      e.preventDefault();
      e.stopPropagation();

      const isRemove = isSelected(value);
      const items = isRemove ? removeItem(value) : [...selectedItems, value];

      setSelectedItems(items);
    },
    [isSelected, removeItem, selectedItems]
  );

  const onSelectAll = useCallback(() => {
    if (!items) return;

    if (selectedItems.length > 0) {
      setSelectedItems([]);
      return;
    }
    const values = items.map((x) => {
      if (typeof x === 'string' || typeof x === 'number') {
        return x;
      }

      return x[valueMember];
    });

    setSelectedItems(values);
    onChange(values as string[] & number[]);
  }, [items, onChange, valueMember, selectedItems]);

  const onRemoveItem = useCallback(
    (value: string | number) => {
      const items = selectedItems.filter((x) => !isEqual(x, value));
      setSelectedItems(items);
      onChange(items as string[] & number[]);
    },
    [selectedItems, onChange, isEqual]
  );

  const addCustomValue = useCallback(() => {
    const valueToAdd = customValue.trim();
    if (!valueToAdd || selectedItems.some((item) => isEqual(item, valueToAdd))) {
      return;
    }

    const items = [...selectedItems, valueToAdd];
    setSelectedItems(items);
    onChange(items as string[] & number[]);
    setCustomValue('');
  }, [customValue, isEqual, onChange, selectedItems]);

  const content = useMemo(() => {
    if (!items || !selectedItems || selectedItems.length === 0) return '';

    return selectedItems.map((x) => {
      const item = items.find((i) => {
        if (typeof i === 'string' || typeof i === 'number') {
          return isEqual(i, x);
        }
        return isEqual(i[valueMember], x);
      });
      const displayName =
        typeof item === 'string' || typeof item === 'number' ? item : item?.[displayMember];
      return (
        <Badge
          color={badgeColor}
          key={String(x)}
          dismissAriaLabel={`Remove ${displayName} from selection`}
          onDismiss={() => onRemoveItem(x)}
        >
          {displayName}
        </Badge>
      );
    });
  }, [selectedItems, displayMember, valueMember, items, onRemoveItem, isEqual, badgeColor]);

  const onCheck = useCallback(
    (value: string | number) => {
      if (isSelected(value)) {
        setSelectedItems(removeItem(value));
      } else {
        setSelectedItems([...selectedItems, value]);
      }
    },
    [selectedItems, isSelected, removeItem]
  );

  const [filter, setFilter] = useState(null);

  const normalizedFilter = (filter ?? '').toLowerCase().trim();
  const filterTerms = normalizedFilter.split(/\s+/);

  const filteredItems = items.filter((item) => {
    const primaryText = (item['searchText'] ?? item[displayMember])?.toLowerCase() || '';
    const secondaryText = secondaryDisplayMember
      ? item[secondaryDisplayMember]?.toLowerCase() || ''
      : '';

    const matchesFilter = (text) =>
      filterTerms.every((term) => text.split(/\s+/).some((word) => word.startsWith(term)));

    return matchesFilter(primaryText) || matchesFilter(secondaryText);
  });

  return (
    <View paddingBottom={removeLegacyPadding ? 0 : 2}>
      <DropdownMenu
        width="trigger"
        active={isOpen}
        onOpen={toggleOpen}
        onClose={onClose}
        trapFocusMode="selection-menu"
      >
        <DropdownMenu.Trigger>
          {(attributes) => (
            <FormControl hasError={submitted && !!errorText}>
              {label && <FormControl.Label>{label}</FormControl.Label>}
              <ZincSelect
                placeholder={placeholder}
                inputAttributes={{ ...attributes, className: 'max-h-[100px]' }}
                className={`${className ?? ''} ${submitted && !!errorText ? 'border !border-orange-300' : 'border'}`.trim()}
              >
                {content && (
                  <View wrap={true} direction="row" gap={1}>
                    {content}
                  </View>
                )}
              </ZincSelect>
              {errorText && submitted && <FormControl.Error>{errorText}</FormControl.Error>}
            </FormControl>
          )}
        </DropdownMenu.Trigger>
        <DropdownMenu.Content attributes={{ role: 'menu' }}>
          {allowCustomValues && (
            <View paddingBottom={2}>
              <TextField
                name="add-custom-value"
                placeholder={customValuePlaceholder}
                value={customValue}
                inputAttributes={{
                  autoComplete: 'off',
                  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      addCustomValue();
                    }
                  }
                }}
                endSlot={
                  <Button
                    variant="ghost"
                    size="small"
                    disabled={!customValue.trim()}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      addCustomValue();
                    }}
                  >
                    Add
                  </Button>
                }
                onChange={({ value }) => setCustomValue(value)}
              />
            </View>
          )}
          {items.length > 10 && (
            <View paddingBottom={2}>
              <TextField
                name="filter"
                placeholder="Search in list"
                value={filter ?? ''}
                inputAttributes={{ autoComplete: 'off' }}
                endSlot={
                  filter && (
                    <Button
                      variant="ghost"
                      size="small"
                      icon={<FontAwesomeIcon icon={faX} />}
                      onClick={() => setFilter(null)}
                    />
                  )
                }
                onChange={({ value }) => setFilter(value)}
              />
            </View>
          )}
          {filteredItems.length === 0 && (
            <View padding={2}>
              <Text color="neutral-faded">No items found</Text>
            </View>
          )}
          <DropdownMenu.Section>
            <VirtualList
              items={filteredItems}
              height={300}
              itemHeight={secondaryDisplayMember ? 46 : 35}
              ItemTemplate={forwardRef(({ item, style, ...other }, ref) => (
                <View attributes={{ style, ref, ...other }}>
                  <DropdownMenu.Item
                    disabled={item['disabled'] ?? false}
                    stopPropagation={true}
                    attributes={{
                      onMouseDown: onItemInteractionStart,
                      onPointerDown: onItemInteractionStart,
                      onClickCapture: onItemInteractionStart,
                      onKeyDown: onItemKeyDown
                    }}
                    onClick={(e) => onSelect(e, item[valueMember] ?? item)}
                  >
                    <View direction="row" gap={2} align="center" wrap={false}>
                      <ZincCheckbox
                        checked={isSelected(item[valueMember] ?? item)}
                        onBlur={() => onCheck(item[valueMember] ?? item)} //Hack to toggle on checkbox click
                      />
                      <div>
                        <Text className="text-ellipsis text-nowrap">
                          {item[displayMember] ?? item}
                        </Text>
                        {secondaryDisplayMember && item[secondaryDisplayMember] && (
                          <Text variant="caption-1" color="neutral-faded" className="mb-[-8px]">
                            {item[secondaryDisplayMember]}
                          </Text>
                        )}
                      </div>
                    </View>
                  </DropdownMenu.Item>
                </View>
              ))}
            />
          </DropdownMenu.Section>
          {(showSelectAll || showApplyButton) && (
            <DropdownMenu.Section>
              <View className="flex flex-row justify-center">
                {showSelectAll && (
                  <Button className="grow" variant="ghost" onClick={onSelectAll}>
                    {selectedItems.length === 0 ? 'Select all' : 'Clear'}
                  </Button>
                )}
                {showApplyButton && (
                  <Button className="grow" variant="ghost" onClick={onClose}>
                    {applyLabel}
                  </Button>
                )}
              </View>
            </DropdownMenu.Section>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
    </View>
  );
};

export default FieldMultiSelect;
